// GuidedJourneyWidget — top-of-dashboard "Your Next Step" mentor card.
// Fetches /api/journey/recommended and shows current progress + next actionable step.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import * as Lucide from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

export default function GuidedJourneyWidget() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/journey/recommended").then((r) => setData(r.data)).catch(() => setData({ journey: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const j = data.journey;
  if (!j) return null;

  const steps = (j.steps || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const completed = new Set(data.progress?.completed_step_ids || []);
  const total = steps.length;
  const done = steps.filter((s) => completed.has(s.step_id)).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const nextStep = steps.find((s) => !completed.has(s.step_id)) || steps[steps.length - 1];

  const openWA = (tpl) => {
    const num = "60137003381"; // global default; picks up admin-editable via community endpoint elsewhere
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(tpl || "Hi MIRESCO!")}`, "_blank", "noopener");
  };

  const completeStep = async () => {
    if (!nextStep) return;
    setBusy(true);
    try {
      await api.post(`/journey/${j.slug}/start`).catch(() => {}); // auto-enroll if not
      await api.post(`/journey/${j.slug}/steps/${nextStep.step_id}/complete`);
      load();
      toast.success(pct + total ? `Step done — keep going!` : "Journey complete!");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doStepAction = () => {
    if (nextStep.kind === "cta_wa" && nextStep.wa_template) return openWA(nextStep.wa_template);
    if (nextStep.external_url) return window.open(nextStep.external_url, nextStep.external_url.startsWith("/") ? "_self" : "_blank");
    if (nextStep.download_url) return window.open(nextStep.download_url, "_blank");
    if (nextStep.video_url) return window.open(nextStep.video_url, "_blank");
  };

  return (
    <div className="border-2 border-zinc-900 bg-white p-5 lg:p-6 shadow-[6px_6px_0_0_#C8102E]" data-testid="guided-journey-widget">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Your Journey</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl mt-2 tracking-tight leading-[1.05]" data-testid="gj-title">{j.title}</h2>
          {j.subtitle && <p className="text-sm text-zinc-600 mt-1">{j.subtitle}</p>}
        </div>
        <Link to={`/journey/${j.slug}`} className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 hover:border-zinc-900 px-3 py-2" data-testid="gj-view-journey">Open Journey</Link>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Progress</div>
          <div className="text-xs font-display font-black" style={{ color: RED }} data-testid="gj-progress-text">{done} / {total} steps · {pct}%</div>
        </div>
        <div className="h-2 bg-zinc-100 border border-zinc-200 overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${RED} 0%, ${GOLD} 100%)` }} data-testid="gj-progress-bar" />
        </div>
      </div>

      {/* Next step callout */}
      {nextStep && (
        <div className="mt-5 border-l-4 pl-4 py-2" style={{ borderColor: GOLD }} data-testid="gj-next-step">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Your next step</div>
          <div className="mt-1 font-display font-black text-lg tracking-tight leading-tight">{nextStep.title}</div>
          {nextStep.subtitle && <div className="text-sm text-zinc-600 mt-1">{nextStep.subtitle}</div>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {(nextStep.wa_template || nextStep.external_url || nextStep.download_url || nextStep.video_url) && (
              <button onClick={doStepAction} className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1" style={{ background: CHARCOAL }} data-testid="gj-step-action">
                {nextStep.kind === "cta_wa" ? "Send WhatsApp" : nextStep.kind === "video" ? "Watch video" : nextStep.kind === "download" ? "Download" : "Do this step"}
                <Lucide.ArrowRight className="w-3 h-3" />
              </button>
            )}
            <button onClick={completeStep} disabled={busy} className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1 disabled:opacity-50" style={{ background: RED }} data-testid="gj-step-complete">
              {busy ? "Saving…" : "Mark done"} <Lucide.Check className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
